import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listMerchants, type Merchant } from "../api/merchants";

function translateStatus(s: string | undefined): string {
  switch (s) {
    case "disabled": return "Inactivo";
    case "suspended": return "Suspendido";
    case "active":
    default: return "Activo";
  }
}

export function MerchantsListPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<Merchant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const load = useCallback(async (cursor?: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await listMerchants({ limit: 25, cursor });
      setItems((prev) => (cursor ? [...prev, ...res.data] : res.data));
      setNextCursor(res.nextCursor ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No pudimos cargar los comercios");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={1.5}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>
            Comercios
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Cuentas que pueden cobrar a través de Novasis Pay.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => load()}
            disabled={loading}
            sx={{ flex: { xs: 1, sm: "0 0 auto" } }}
          >
            Refrescar
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/merchants/new")}
            sx={{ flex: { xs: 1, sm: "0 0 auto" } }}
          >
            Nuevo comercio
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      {/* Mobile: tarjetas. Una tabla compacta no es legible a 430px. */}
      <Stack spacing={1.5} sx={{ display: { xs: "flex", md: "none" } }}>
        {items.map((m) => (
          <Card key={m.id} variant="outlined">
            <CardActionArea onClick={() => navigate(`/merchants/${m.id}`)}>
              <CardContent>
                <Stack spacing={0.75}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                      {m.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={translateStatus(m.status)}
                      color={m.status === "disabled" ? "default" : "success"}
                      variant={m.status === "disabled" ? "outlined" : "filled"}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                    {m.email}
                  </Typography>
                  <Stack direction="row" spacing={1.5} flexWrap="wrap">
                    <Typography variant="caption" color="text.secondary">
                      🌎 {m.defaultCountry}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      📅 {new Date(m.createdAt).toLocaleDateString()}
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
        {!loading && items.length === 0 && (
          <Paper variant="outlined" sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
            Todavía no hay comercios. Creá el primero con "Nuevo comercio".
          </Paper>
        )}
      </Stack>

      {/* Desktop / tablet: tabla completa. */}
      <Paper variant="outlined" sx={{ display: { xs: "none", md: "block" }, overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>País</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Creado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((m) => (
              <TableRow
                key={m.id}
                hover
                sx={{ cursor: "pointer" }}
                onClick={() => navigate(`/merchants/${m.id}`)}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {m.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {m.id}
                  </Typography>
                </TableCell>
                <TableCell>{m.email}</TableCell>
                <TableCell>{m.defaultCountry}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={m.status ?? "active"}
                    color={m.status === "disabled" ? "default" : "success"}
                    variant={m.status === "disabled" ? "outlined" : "filled"}
                  />
                </TableCell>
                <TableCell>{new Date(m.createdAt).toLocaleDateString()}</TableCell>
              </TableRow>
            ))}
            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                  Todavía no hay comercios. Creá el primero con "Nuevo comercio".
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={22} />
          </Box>
        )}
      </Paper>

      {loading && (
        <Box sx={{ display: { xs: "flex", md: "none" }, justifyContent: "center" }}>
          <CircularProgress size={22} />
        </Box>
      )}

      {nextCursor && (
        <Stack direction="row" justifyContent="center">
          <Button onClick={() => load(nextCursor)} disabled={loading}>
            Cargar más
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
